var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { Field } from 'rc-field-form';
import FieldContext from 'rc-field-form/lib/FieldContext';
import { supportRef } from 'rc-util/lib/ref';
import omit from 'omit.js';
import Row from '../grid/row';
import { ConfigContext } from '../config-provider';
import { tuple } from '../_util/type';
import devWarning from '../_util/devWarning';
import FormItemLabel from './FormItemLabel';
import FormItemInput from './FormItemInput';
import { FormContext, FormItemContext } from './context';
import { toArray, getFieldId } from './util';
import { cloneElement, isValidElement } from '../_util/reactNode';
import useFrameState from './hooks/useFrameState';
import useItemRef from './hooks/useItemRef';
const ValidateStatuses = tuple('success', 'warning', 'error', 'validating', '');
const MemoInput = React.memo(({ children }) => children, (prev, next) => {
    return prev.value === next.value && prev.update === next.update;
});
function hasValidName(name) {
    if (name === null) {
        if (process.env.NODE_ENV !== 'production')
            devWarning(false, 'Form.Item', '`null` is passed as `name` property');
    }
    return !(name === undefined || name === null);
}
function FormItem(props) {
    const { name, fieldKey, noStyle, dependencies, prefixCls: customizePrefixCls, style, className, shouldUpdate, hasFeedback, help, rules, validateStatus, children, required, label, trigger = 'onChange', validateTrigger, hidden } = props, restProps = __rest(props, ["name", "fieldKey", "noStyle", "dependencies", "prefixCls", "style", "className", "shouldUpdate", "hasFeedback", "help", "rules", "validateStatus", "children", "required", "label", "trigger", "validateTrigger", "hidden"]);
    const destroyRef = React.useRef(false);
    const { getPrefixCls } = React.useContext(ConfigContext);
    const { name: formName } = React.useContext(FormContext);
    const { updateItemErrors } = React.useContext(FormItemContext);
    const [domErrorVisible, innerSetDomErrorVisible] = React.useState(!!help);
    const prevValidateStatusRef = React.useRef(validateStatus);
    const [inlineErrors, setInlineErrors] = useFrameState({});
    const { validateTrigger: contextValidateTrigger } = React.useContext(FieldContext);
    const mergedValidateTrigger = validateTrigger !== undefined ? validateTrigger : contextValidateTrigger;
    function setDomErrorVisible(visible) {
        if (!destroyRef.current) {
            innerSetDomErrorVisible(visible);
        }
    }
    const hasName = hasValidName(name);
    // Cache Field NamePath
    const nameRef = React.useRef([]);
    // Should clean up if Field removed
    React.useEffect(() => {
        return () => {
            destroyRef.current = true;
            updateItemErrors(nameRef.current.join('__SPLIT__'), []);
        };
    }, []);
    const prefixCls = getPrefixCls('form', customizePrefixCls);
    // ======================== Errors ========================
    // Collect noStyle Field error to the top FormItem
    const updateChildItemErrors = noStyle
        ? updateItemErrors
        : (subName, subErrors) => {
            if (!isEqual(inlineErrors[subName], subErrors)) {
                setInlineErrors(prevInlineErrors => (Object.assign(Object.assign({}, prevInlineErrors), { [subName]: subErrors })));
            }
        };
    // ===================== Children Ref =====================
    const getItemRef = useItemRef();
    function renderLayout(baseChildren, fieldId, meta, isRequired) {
        if (noStyle) {
            return baseChildren;
        }
        // ======================== Errors ========================
        let mergedErrors;
        if (help !== undefined && help !== null) {
            mergedErrors = toArray(help);
        }
        else {
            mergedErrors = meta ? meta.errors : [];
            Object.keys(inlineErrors).forEach(subName => {
                const subErrors = inlineErrors[subName] || [];
                if (subErrors.length) {
                    mergedErrors = [...mergedErrors, ...subErrors];
                }
            });
        }
        // ======================== Status ========================
        let mergedValidateStatus = '';
        if (validateStatus !== undefined) {
            mergedValidateStatus = validateStatus;
        }
        else if (meta && meta.validating) {
            mergedValidateStatus = 'validating';
        }
        else if (!help && mergedErrors.length) {
            mergedValidateStatus = 'error';
        }
        else if (meta && meta.touched) {
            mergedValidateStatus = 'success';
        }
        if (domErrorVisible && help) {
            prevValidateStatusRef.current = mergedValidateStatus;
        }
        const itemClassName = {
            [`${prefixCls}-item`]: true,
            [`${prefixCls}-item-with-help`]: domErrorVisible || help,
            [`${className}`]: !!className,
            // Status
            [`${prefixCls}-item-has-feedback`]: mergedValidateStatus && hasFeedback,
            [`${prefixCls}-item-has-success`]: mergedValidateStatus === 'success',
            [`${prefixCls}-item-has-warning`]: mergedValidateStatus === 'warning',
            [`${prefixCls}-item-has-error`]: mergedValidateStatus === 'error',
            [`${prefixCls}-item-has-error-leave`]: !help && domErrorVisible && prevValidateStatusRef.current === 'error',
            [`${prefixCls}-item-is-validating`]: mergedValidateStatus === 'validating',
            [`${prefixCls}-item-hidden`]: hidden,
        };
        // ======================= Children =======================
        return (<Row className={classNames(itemClassName)} style={style} key="row" {...omit(restProps, [
            'colon',
            'extra',
            'getValueFromEvent',
            'getValueProps',
            'hasFeedback',
            'help',
            'htmlFor',
            'id',
            'initialValue',
            'isListField',
            'label',
            'labelAlign',
            'labelCol',
            'normalize',
            'required',
            'validateFirst',
            'validateStatus',
            'valuePropName',
            'wrapperCol',
        ])}>
        
        <FormItemLabel htmlFor={fieldId} required={isRequired} {...props} prefixCls={prefixCls}/>
        
        <FormItemInput {...props} {...meta} errors={mergedErrors} prefixCls={prefixCls} onDomErrorVisibleChange={setDomErrorVisible} validateStatus={mergedValidateStatus}>
          <FormItemContext.Provider value={{ updateItemErrors: updateChildItemErrors }}>
            {baseChildren}
          </FormItemContext.Provider>
        </FormItemInput>
      </Row>);
    }
    const isRenderProps = typeof children === 'function';
    // Record for real component render
    const updateRef = React.useRef(0);
    updateRef.current += 1;
    if (!hasName && !isRenderProps && !dependencies) {
        return renderLayout(children);
    }
    const variables = {};
    if (typeof label === 'string') {
        variables.label = label;
    }
    return (<Field {...props} messageVariables={variables} trigger={trigger} validateTrigger={mergedValidateTrigger} onReset={() => {
        setDomErrorVisible(false);
    }}>
      {(control, meta, context) => {
        const { errors } = meta;
        const mergedName = toArray(name).length && meta ? meta.name : [];
        const fieldId = getFieldId(mergedName, formName);
        if (noStyle) {
            nameRef.current = [...mergedName];
            if (fieldKey) {
                const fieldKeys = Array.isArray(fieldKey) ? fieldKey : [fieldKey];
                nameRef.current = [...mergedName.slice(0, -1), ...fieldKeys];
            }
            updateItemErrors(nameRef.current.join('__SPLIT__'), errors);
        }
        const isRequired = required !== undefined
            ? required
            : !!(rules &&
                rules.some(rule => {
                    if (rule && typeof rule === 'object' && rule.required) {
                        return true;
                    }
                    if (typeof rule === 'function') {
                        const ruleEntity = rule(context);
                        return ruleEntity && ruleEntity.required;
                    }
                    return false;
                }));
        // ======================= Children =======================
        const mergedControl = Object.assign({}, control);
        let childNode = null;
        if (Array.isArray(children) && hasName) {
            devWarning(false, 'Form.Item', '`children` is array of render props cannot have `name`.');
            childNode = children;
        }
        else if (isRenderProps && (!shouldUpdate || hasName)) {
            devWarning(!!shouldUpdate, 'Form.Item', '`children` of render props only work with `shouldUpdate`.');
            devWarning(!hasName, 'Form.Item', "Do not use `name` with `children` of render props since it's not a field.");
        }
        else if (dependencies && !isRenderProps && !hasName) {
            devWarning(false, 'Form.Item', 'Must set `name` or use render props when `dependencies` is set.');
        }
        else if (isValidElement(children)) {
            devWarning(children.props.defaultValue === undefined, 'Form.Item', '`defaultValue` will not work on controlled Field. You should use `initialValues` of Form instead.');
            const childProps = Object.assign(Object.assign({}, children.props), mergedControl);
            if (!childProps.id) {
                childProps.id = fieldId;
            }
            if (supportRef(children)) {
                childProps.ref = getItemRef(mergedName, children);
            }
            // We should keep user origin event handler
            const triggers = new Set([
                ...toArray(trigger),
                ...toArray(mergedValidateTrigger),
            ]);
            triggers.forEach(eventName => {
                childProps[eventName] = (...args) => {
                    var _a, _b, _c;
                    (_a = mergedControl[eventName]) === null || _a === void 0 ? void 0 : _a.call(mergedControl, ...args);
                    (_c = (_b = children.props)[eventName]) === null || _c === void 0 ? void 0 : _c.call(_b, ...args);
                };
            });
            childNode = (<MemoInput value={mergedControl[props.valuePropName || 'value']} update={updateRef.current}>
              {cloneElement(children, childProps)}
            </MemoInput>);
        }
        else if (isRenderProps && shouldUpdate && !hasName) {
            childNode = children(context);
        }
        else {
            devWarning(!mergedName.length, 'Form.Item', '`name` is only used for validate React element. If you are using Form.Item as layout display, please remove `name` instead.');
            childNode = children;
        }
        return renderLayout(childNode, fieldId, meta, isRequired);
    }}
    </Field>);
}
export default FormItem;
